/**
 * Scope-item ↔ symbol ↔ cable-type wiring — Projects v2 Phase 5 (P5.3).
 *
 * The bridge between three previously-unrelated catalogues:
 *
 *   1. `EQUIPMENT_CATEGORIES` (field-scope-configs.ts) — what techs
 *      declare during the pre-site walkthrough. Items keyed by short
 *      ids like `'ap'`, `'doorbell'`, `'starlink-dish'`.
 *
 *   2. `SYMBOLS` (data/symbol-icons/index.ts) — what the PM drops on
 *      the floor plan. Keyed by id like `'ap-ceiling'`, `'doorbell'`,
 *      `'nbn-ntd'`.
 *
 *   3. Cable-type free-text hints — what each symbol naturally pulls
 *      (`SymbolDefinition.defaultCableType`).
 *
 * P5.3 needs this bridge to:
 *
 *   - Spawn N cable-run stubs when a zone declares "N APs" → one
 *     stub per declared scope-item, cable-type pre-filled.
 *   - Auto-link an unlinked stub when the PM drops an AP symbol on
 *     the plan in the same dwelling — the match goes both ways.
 *
 * Adding a scope item: append to `SCOPE_ITEM_MAP` below. Scope items
 * NOT in the map are silently skipped during stub generation
 * (e.g. `'mount-pole'` — not a cabled device, no stub needed).
 */

import { getSymbol } from './symbol-icons';

/** One entry per scope-item id that should spawn a cable run. */
export interface ScopeItemCableMapping {
  /** Scope item id from `field-scope-configs.ts` `EQUIPMENT_CATEGORIES`. */
  scopeItemId: string;
  /** Human label — surfaces in stub row labels + error messages. */
  scopeItemLabel: string;
  /** Symbol ids (from `symbol-icons`) that satisfy this scope item.
   *  The order matters for auto-linking: a placed symbol whose id is
   *  earlier in the list takes precedence. Most items map to exactly
   *  one symbol — multi-symbol entries are intentional (e.g. a
   *  generic "Access Point" scope item is satisfied by either a
   *  ceiling AP OR a wall AP). */
  symbolIds: string[];
  /** Cable type hint for the stub (free text — matches the cable-
   *  catalog search heuristic in `findCableCatalogByTypeName`). When
   *  omitted, the entry's first symbol's `defaultCableType` wins;
   *  when that's also missing, the stub gets no cable type and the
   *  PM picks one manually via the P5.1 detail panel. */
  defaultCableType?: string;
}

/** Curated scope-item → symbol/cable-type wiring. The list is short
 *  enough to be hand-maintained — keeping it that way (rather than
 *  inferring via fuzzy match) means the auto-link UX never produces
 *  surprises. */
export const SCOPE_ITEM_MAP: ScopeItemCableMapping[] = [
  // Networking — APs cable as Cat6A; switches + wall plates as Cat6.
  { scopeItemId: 'ap',          scopeItemLabel: 'Access Point',  symbolIds: ['ap-ceiling', 'ap-wall'],      defaultCableType: 'Cat6A' },
  { scopeItemId: 'switch',      scopeItemLabel: 'Switch',        symbolIds: ['switch-8port'],                defaultCableType: 'Cat6A' },
  { scopeItemId: 'wall-plate',  scopeItemLabel: 'Wall Plate',    symbolIds: ['data-wall-plate'],             defaultCableType: 'Cat6' },

  // Internet / structured — NBN NTD pulls Cat6 from the switch; the
  // dish + mesh are wireless from the modem so no stub today.
  { scopeItemId: 'modem',       scopeItemLabel: 'Modem / NTD',   symbolIds: ['nbn-ntd'],                     defaultCableType: 'Cat6' },

  // Security — cameras pull Cat6; doorbell + intercom too.
  { scopeItemId: 'outdoor-cam', scopeItemLabel: 'Outdoor Camera',symbolIds: ['bullet-camera', 'dome-camera'], defaultCableType: 'Cat6' },
  { scopeItemId: 'indoor-cam',  scopeItemLabel: 'Indoor Camera', symbolIds: ['dome-camera'],                  defaultCableType: 'Cat6' },
  { scopeItemId: 'doorbell',    scopeItemLabel: 'Doorbell',      symbolIds: ['doorbell'],                     defaultCableType: 'Cat6' },
  { scopeItemId: 'intercom',    scopeItemLabel: 'Intercom',      symbolIds: ['intercom-panel'],               defaultCableType: 'Cat6' },
  { scopeItemId: 'motion',      scopeItemLabel: 'Motion Sensor', symbolIds: ['motion-sensor'],                defaultCableType: 'Cat6' },
  { scopeItemId: 'sensor',      scopeItemLabel: 'Door Contact',  symbolIds: ['door-contact'] /* low-voltage; no cable hint */ },

  // Smart home — keypads + switches + relays are typically Cresnet /
  // dedicated low-voltage; leave cable type blank so the PM picks
  // explicitly. They still get a stub, just without a pre-filled type.
  { scopeItemId: 'smart-switch',scopeItemLabel: 'Smart Switch',  symbolIds: ['smart-switch'] },
  { scopeItemId: 'dimmer',      scopeItemLabel: 'Dimmer',        symbolIds: ['dimmer'] },
];

const BY_SCOPE_ITEM_ID = new Map(SCOPE_ITEM_MAP.map((e) => [e.scopeItemId, e]));
const BY_SYMBOL_ID = (() => {
  const map = new Map<string, ScopeItemCableMapping>();
  for (const entry of SCOPE_ITEM_MAP) {
    for (const symbolId of entry.symbolIds) {
      // First scope item that claims a symbol wins. Today the table
      // has no overlapping symbols, so this is deterministic — the
      // log warns if a future edit introduces one.
      if (map.has(symbolId)) {
         
        console.warn(
          `[scope-item-to-cable-type-map] symbol "${symbolId}" claimed by multiple scope items; keeping first.`
        );
        continue;
      }
      map.set(symbolId, entry);
    }
  }
  return map;
})();

/** Lookup by scope-item id. Returns undefined for items that have no
 *  cable mapping (skip during stub generation). */
export function getScopeItemMapping(scopeItemId: string): ScopeItemCableMapping | undefined {
  return BY_SCOPE_ITEM_ID.get(scopeItemId);
}

/** Reverse lookup — given a symbol id placed on the plan, returns
 *  the scope-item entry it satisfies (or undefined if the symbol
 *  doesn't map to any tracked scope item — e.g. a generic "rack"
 *  symbol doesn't auto-link to anything). */
export function getMappingBySymbolId(symbolId: string): ScopeItemCableMapping | undefined {
  return BY_SYMBOL_ID.get(symbolId);
}

/** Resolve the cable-type hint for a scope item. Prefers the explicit
 *  `defaultCableType` on the entry; falls back to the first symbol's
 *  `SymbolDefinition.defaultCableType`. Returns undefined when both
 *  are missing — the caller treats that as "PM picks manually". */
export function resolveScopeItemCableType(scopeItemId: string): string | undefined {
  const entry = BY_SCOPE_ITEM_ID.get(scopeItemId);
  if (!entry) return undefined;
  if (entry.defaultCableType) return entry.defaultCableType;
  for (const symbolId of entry.symbolIds) {
    const sym = getSymbol(symbolId);
    if (sym?.defaultCableType) return sym.defaultCableType;
  }
  return undefined;
}
